import { AccessReportMaintJobStatusEnum } from "@app/access-reports/admin";
import { PciDashboardStatusEnum } from "@app/projects/network-assessment/pci-dashboard/shared"
import { EmployeeGroup } from "@app/access-reports/shared/employeeGroup";
import { AccessReportStatusEnum } from "@app/access-reports/dashboard";
import { AccessReportRunStatusChangeNotificationMessagePayload } from "@app/access-reports/dashboard/shared/accessReportRunStatusChangeNotificationMessagePayload";
import { AccessReportUsageReportChangeNotificationMessagePayload } from "@app/access-reports/dashboard/shared/accessReportUsageReportChangeNotificationMessagePayload";
import { JobType } from "@app/jobs/jobType";
import { JobStatus } from "@app/jobs/jobStatus";
import { ProjectRunConfigurationStatus, AccuracyGapAnalysisStatus, ProjectRunStatusEnum } from "@app/projects/enumerators";
import { SummaryResultNetworkType } from "@app/shared/data-summaries";

/** The possible types of messages the notification context can receive. */
enum MessageType {
  SystemMaintenance = "systemMaintenance",
  JobStatusChange = "jobStatusChange",
  SummariesResultsAvailable = "summariesResultsAvailable",
  ReportComplete = "reportComplete",
  ProjectRunStatusChange = "projectRunStatusChange",
  ClientGapLinkResultsAvailable = "clientGapLinkResultsAvailable",
  ProcessingProjectionAnalysis = "processingProjectionAnalysis",
  ProcessingProviderChanges = "processingProviderChanges",
  ProcessingSpecialtyGroupExceptions = "processingSpecialtyGroupExceptions",
  MultiProjectRunStatusChange = "multiProjectRunStatusChange",
  AccessReportsRunStatusChange = "accessReportsRunStatusChange",
  ProcessingProspectMarketNcp = "processingProspectMarketNcp",
  AccuracyGapAnalysisRunStatusChange = "accuracyGapAnalysisRunStatusChange",
  AccessReportsEmployeeGroupUploadStatusChange = "accessReportsEmployeeGroupUploadStatusChange",
  AccessReportsUsageReportStatusChange = "accessReportsUsageReportStatusChange",
  BulkTargetProjectRunStatusChange = "bulkTargetProjectRunStatusChange",
  AccessReportsProviderSetCopyStatusChange = "accessReportsProviderSetCopyStatusChange",
  PciDashboardStatusChange = "pciDashboardStatusChange",
  ZipCodeAnalysisStatusChangeNotification = "zipCodeAnalysisStatusChange",
  ProviderValidationStatusChange = "providerValidationStatusChange",
  MemberSetActivationJobStatusChange = "memberSetActivationJobStatusChange",
  ZipCodeAnalysisNcpStatusChange = "zipCodeAnalysisNcpStatusChange",
  ComplianceConnectComment = "complianceConnectComment",
  AiChatProgress = "aiChatProgress",
}

type PayloadUnionType = JobStatusChangePayload
  | SummariesResultsAvailablePayload
  | ReportCompletePayload
  | ProjectRunStatusChangePayload
  | ProcessingSpecialtyGroupExceptionsPayload
  | MultiProjectRunStatusChangePayload
  | AccessReportRunStatusChangeNotificationMessagePayload
  | AccessReportsEmployeeGroupUploadStatusChangePayload
  | AccessReportUsageReportChangeNotificationMessagePayload
  | AccessReportMaintJobStatusChangeNotificationMessagePayload
  | PciDashboardStatusChangeNotificationMessagePayload
  | ZipCodeAnalysisStatusChangeNotificationMessagePayload
  | ProviderValidationStatusChangeNotificationMessagePayload
  | MemberSetActivationJobStatusChangeNotificationMessagePayload
  | ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload
  | ComplianceConnectCommentNotificationMessagePayload
  | AiChatProgressNotificationMessagePayload;

/** Represents a message received via our SignalR/WebSocket pipe. */
interface Message {
  notificationType: MessageType;
  targetUserId?: number;
  payload?: PayloadUnionType;
}

interface JobStatusChangePayload {
  jobId: number;
  userId: number;
  clientId: number;
  jobType: JobType;
  projectId?: number;
  payload?: string;
  dateCreated: Date;
  dateQueued?: Date;
  dateStarted?: Date;
  dateCancelled?: Date;
  dateFinished?: Date;
  success?: boolean;
  errorMessage?: boolean;

  jobStatus: JobStatus;
}

interface ClientGapResultsAvailableNotificationMessagePayload {
  clientId: number;
  jobStatus: JobStatus;
}

interface SummariesResultsAvailablePayload {
  projectId: number;
  projectRunConfigurationId?: number;
  networkType: SummaryResultNetworkType;
}
interface ReportCompletePayload {
  reportId: number;
  reportName: string;
}
interface ProjectRunStatusChangePayload {
  projectId: number;
  projectRunConfigurationId: number;
  status: ProjectRunConfigurationStatus;
  isPublished: boolean;
  isReadOnly: boolean;
  isEditRestricted: boolean;
}
type ProcessingSpecialtyGroupExceptionsPayload = ProjectRunStatusChangePayload;

interface MultiProjectRunStatusChangePayload {
  customProjectIds: number[];
  isPublished: boolean;
  status: ProjectRunConfigurationStatus;
  readOnly: boolean;
}

interface AccessReportsRunStatusChangePayload {
  accessReportId: number;
  jobStatus: JobStatus;
}

interface AccuracyGapAnalysisStatusChangeNotificationMessagePayload {
  projectId?: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  accuracyGapAnalysisStatus?: AccuracyGapAnalysisStatus;
}

interface AccessReportsEmployeeGroupUploadStatusChangePayload extends EmployeeGroup {
  status: AccessReportStatusEnum;
  uploadMessage: string;
}

interface BulkTargetProjectRunStatusChangeNotificationMessagePayload {
  projectId?: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  accuracyGapAnalysisStatus?: AccuracyGapAnalysisStatus;
  status: ProjectRunConfigurationStatus;
}

interface AccessReportMaintJobStatusChangeNotificationMessagePayload {
  providerSetCopyId: number;
  providerSetId: number;
  providerSetVersionId: number;
  jobStatus: AccessReportMaintJobStatusEnum
}

interface PciDashboardStatusChangeNotificationMessagePayload {
  projectId: number
  projectRunConfigurationId: number
  status: PciDashboardStatusEnum
}

interface ZipCodeAnalysisStatusChangeNotificationMessagePayload {
  projectId: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  projectRunStatus: ProjectRunConfigurationStatus & ProjectRunStatusEnum;
}

interface ProviderValidationStatusChangeNotificationMessagePayload {
  providerSetId: number;
  providerSetVersionId: number;
}

interface MemberSetActivationJobStatusChangeNotificationMessagePayload {
  jobStatus: JobStatus;
  clientId: number;
  userId: number;
  memberSetId: number;
  memberSetVersionId: number;
}

interface ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload {
  numberOfJobs?: number;
  projectRunConfigurationId: number;
  // the API is actually sending a ProjectRunStatusEnum, but zip projects
  // expect a ProjectRunConfigurationStatus. There is tech debt to address this.
  projectRunStatus: ProjectRunConfigurationStatus;
}

interface ComplianceConnectCommentNotificationMessagePayload {
  submissionId: number;
  submissionCommentId: number;
  authorUserId: number;
  /** Matches ClientType enum: 1 = Issuer, 2 = Regulator */
  authorRole: number;
  commentText: string;
  dateCreated: string;
}

interface AiChatProgressNotificationMessagePayload {
  threadId: string;
  executorId: string;
  status: string;
  timestamp: string;
}

export {
  ClientGapResultsAvailableNotificationMessagePayload,
  JobStatusChangePayload,
  Message as default,
  MessageType,
  MultiProjectRunStatusChangePayload,
  ProjectRunStatusChangePayload,
  ReportCompletePayload,
  SummariesResultsAvailablePayload,
  AccessReportsRunStatusChangePayload,
  AccuracyGapAnalysisStatusChangeNotificationMessagePayload,
  AccessReportMaintJobStatusChangeNotificationMessagePayload,
  AccessReportsEmployeeGroupUploadStatusChangePayload,
  BulkTargetProjectRunStatusChangeNotificationMessagePayload,
  PciDashboardStatusChangeNotificationMessagePayload,
  ZipCodeAnalysisStatusChangeNotificationMessagePayload,
  ProviderValidationStatusChangeNotificationMessagePayload,
  ProcessingSpecialtyGroupExceptionsPayload,
  PayloadUnionType,
  MemberSetActivationJobStatusChangeNotificationMessagePayload,
  ZipCodeAnalysisNcpStatusChangeNotificationMessagePayload,
  ComplianceConnectCommentNotificationMessagePayload,
  AiChatProgressNotificationMessagePayload,
};
